import React from 'react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const TruckingAccidents = () => {
  return (
    <div data-testid="trucking-accidents-page">
      {/* Hero */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-heading font-bold text-slate-900 mb-6 tracking-tight">
            Trucking Accidents
          </h1>
          <p className="text-base md:text-xl text-slate-600 leading-relaxed">
            Commercial truck accidents often result in catastrophic injuries due to the size and weight 
            of these vehicles. These cases involve complex regulations, multiple parties, and aggressive 
            defense tactics. You need experienced attorneys who know how to win.
          </p>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            How We Help
          </h2>
          <div className="space-y-4 text-slate-600 leading-relaxed">
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Immediate preservation of critical evidence including black box data, logbooks, and maintenance records</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Thorough investigation of federal and state trucking regulations and whether they were violated</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Identification of all liable parties (driver, trucking company, owner, maintenance provider, etc.)</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Analysis of driver qualifications, training, hours of service, and drug/alcohol testing</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Consultation with accident reconstruction experts and industry specialists</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Aggressive pursuit of maximum compensation from all available sources</span>
            </p>
          </div>
        </div>
      </section>

      {/* Trucking Accident Statistics */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            The Danger of Commercial Trucks
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            Large commercial trucks can weigh up to 80,000 pounds—20 times more than a typical passenger vehicle. 
            When a collision occurs, the results are often devastating. Common causes of trucking accidents include:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-slate-600">
            <div className="border-l-4 border-primary pl-4">
              <p className="font-semibold text-slate-900 mb-1">Driver Fatigue</p>
              <p className="text-sm">Hours of service violations and inadequate rest</p>
            </div>
            <div className="border-l-4 border-primary pl-4">
              <p className="font-semibold text-slate-900 mb-1">Distracted Driving</p>
              <p className="text-sm">Phone use, eating, or other inattention</p>
            </div>
            <div className="border-l-4 border-primary pl-4">
              <p className="font-semibold text-slate-900 mb-1">Improper Loading</p>
              <p className="text-sm">Overloaded or unsecured cargo</p>
            </div>
            <div className="border-l-4 border-primary pl-4">
              <p className="font-semibold text-slate-900 mb-1">Poor Maintenance</p>
              <p className="text-sm">Brake failures, tire blowouts, mechanical defects</p>
            </div>
            <div className="border-l-4 border-primary pl-4">
              <p className="font-semibold text-slate-900 mb-1">Speeding</p>
              <p className="text-sm">Excessive speed for conditions or over posted limits</p>
            </div>
            <div className="border-l-4 border-primary pl-4">
              <p className="font-semibold text-slate-900 mb-1">Inadequate Training</p>
              <p className="text-sm">Unqualified or inexperienced drivers</p>
            </div>
          </div>
        </div>
      </section>

      {/* What to Do Next */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            After a Trucking Accident
          </h2>
          <div className="space-y-6">
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Seek Immediate Medical Care
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Your health is the top priority. Even if you don't feel badly injured, see a doctor immediately. 
                Some serious injuries have delayed symptoms.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Contact an Attorney Immediately
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Time is critical in trucking accident cases. Evidence can be lost or destroyed quickly. We act fast 
                to preserve electronic data, logbooks, and other crucial evidence.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Do Not Give Statements
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Trucking companies and their insurers will send investigators immediately. Do not give recorded statements 
                or sign any documents without speaking to an attorney first.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-12 bg-primary text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <p className="text-lg">
            Representing trucking accident victims primarily in <strong>North Carolina</strong> and <strong>South Carolina</strong>. 
            We can also assist with cases in <strong>Tennessee</strong> where applicable.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-1">
                Who can be held liable in a trucking accident?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                Potentially liable parties include the truck driver, trucking company, truck owner (if different), 
                cargo loading company, maintenance providers, truck or parts manufacturers, and other negligent parties. 
                We investigate thoroughly to identify everyone who should be held accountable.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-2">
                What evidence is important in a trucking accident case?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                Critical evidence includes the truck's electronic logging device (ELD) data, driver logs, maintenance 
                and inspection records, driver qualification files, black box data, witness statements, accident scene 
                documentation, and drug/alcohol testing results. Much of this evidence can be lost if not preserved quickly.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-3">
                How are trucking accident cases different from regular car accidents?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                Trucking cases involve federal regulations, more complex liability issues, higher insurance policy limits, 
                and well-funded defense teams. The injuries are typically more severe. These cases require attorneys with 
                specific experience in trucking litigation.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-4">
                What compensation can I recover?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                You may recover damages for medical expenses (past and future), lost wages and earning capacity, property damage, 
                pain and suffering, and other losses. Because trucking accidents often cause catastrophic injuries, the potential 
                damages can be substantial.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-slate-900 mb-6 tracking-tight">
            Time is Critical—Contact Us Now
          </h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Don't delay. Reach out today for a free consultation about your trucking accident case.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-primary text-white px-8 py-4 rounded-sm font-semibold tracking-wider uppercase text-sm shadow-sm hover:bg-primary/90 transition-colors"
              data-testid="cta-consultation"
            >
              Request a Consultation
            </Link>
            <a
              href="tel:+13363541040"
              className="border-2 border-primary text-primary px-8 py-4 rounded-sm font-semibold tracking-wider uppercase text-sm hover:bg-primary/10 transition-colors"
              data-testid="cta-call"
            >
              Call 336-354-1040
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TruckingAccidents;